import React from 'react';
import { makeStyles, useTheme } from "@mui/styles";
import { Box, Skeleton, Typography } from '@mui/material';
import Chip from 'components/Chip';
import HorizontalScrollNavigator from 'components/HorizontalScrollNavigator';
import { NoResultComponent } from './TodoList';
import { useEffect } from 'react';
import { useState } from 'react';
import { useSnackbar } from 'contexts';
import dayjs from 'dayjs';
import StartAssessmentDialog from 'dialogs/StartAssessmentDialog';
import { useSearchParams } from 'react-router-dom';
import { FilterChips } from 'components/Filter';
import { filters } from './PersonalizedInterviewList';
import { Session } from 'utils';
import NoAccessTooltip from 'components/NoAccessTooltip';

const useStyles = makeStyles(theme => ({
  cardContainer: {
    position: "relative",
    padding: theme.spacing(5),
    display: "flex",
    flexDirection: "column",
    justifyContent: 'space-between',
    width: "405px",
    height: "210px",
    border: `3px solid ${theme.palette.neutral['clr-200']}`,
    borderRadius: theme.spacing(2),
    cursor: "pointer",
    flex: "0 0 auto",
    transition: "box-shadow 10ms linear",
    '&:hover': {
      border: `3px solid ${theme.palette.primary['clr-100']}`,
      transition: 'box-shadow 0.1s ease-in-out',
    },
    overflow: 'hidden',
    backgroundColor: theme.palette.shades['clr-white-900']
  },
  disabledCard: {
    position: 'relative',
    padding: theme.spacing(5),
    display: "flex",
    flexDirection: "column",
    justifyContent: 'space-between',
    width: "405px",
    height: "210px",
    border: `3px solid ${theme.palette.neutral['clr-300']}`,
    borderRadius: theme.spacing(2),
    backgroundColor: theme.palette.shades['clr-grey-100'],
    cursor: 'default',
    flex: "0 0 auto",
    transition: "box-shadow 10ms linear",
    overflow: 'hidden',
    '& > *': {
      color: `${theme.palette.neutral['clr-600']} !important`
    },
    '&:hover': {
      border: `3px solid ${theme.palette.neutral['clr-300']}`,
    },
  },
  header: {
    display: 'flex',
    width: '100%',
    justifyContent: 'space-between',
    zIndex: 2
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    zIndex: 2
  },
  truncate: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
}));


const CaseMasterContestCard = ({ template, onExpire, disabled = false }) => {
  const classes = useStyles();
  const theme = useTheme();
  const snackbar = useSnackbar();
  const [searchParams] = useSearchParams();

  const [timeRemaining, setTimeRemaining] = useState('');
  const [status, setStatus] = useState('Upcoming');
  const [startInterview, setStartInterview] = useState(false);
  const [isNeverExpiring, setIsNeverExpiring] = useState(false);

  useEffect(() => {
    if (!template) return;

    const publishDate = dayjs(template.startAt);
    const expiryDate = dayjs(template.endAt);

    const isNever = expiryDate.year() === 9999;
    setIsNeverExpiring(isNever);

    const updateStatusAndTimer = () => {
      const now = dayjs();

      if (isNever) {
        setStatus('Live');
        setTimeRemaining('');
        return;
      }

      let newStatus;
      let timeText = '';

      if (now.isAfter(expiryDate)) {
        newStatus = 'Ended';
        if (status === 'Live' && onExpire) {
          onExpire(template._id);
        }
      } else if (now.isAfter(publishDate) || now.isSame(publishDate)) {
        newStatus = 'Live';
        const timeDiff = expiryDate.diff(now, 'second');
        if (timeDiff > 0) {
          if (timeDiff > 86400) {
            const days = Math.floor(timeDiff / 86400);
            const hours = Math.floor((timeDiff % 86400) / 3600);
            timeText = `${days} Day${days > 1 ? 's' : ''} ${hours} Hr${hours > 1 ? 's' : ''}`;
          } else {
            const hours = Math.floor(timeDiff / 3600);
            const minutes = Math.floor((timeDiff % 3600) / 60);
            const seconds = timeDiff % 60;
            timeText = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
          }
        }
      } else {
        newStatus = 'Upcoming';
        const timeDiff = publishDate.diff(now, 'second');
        if (timeDiff > 0) {
          if (timeDiff > 86400) {
            const days = Math.floor(timeDiff / 86400);
            const hours = Math.floor((timeDiff % 86400) / 3600);
            timeText = `${days} Day${days > 1 ? 's' : ''} ${hours} Hr${hours > 1 ? 's' : ''}`;
          } else {
            const hours = Math.floor(timeDiff / 3600);
            const minutes = Math.floor((timeDiff % 3600) / 60);
            const seconds = timeDiff % 60;
            timeText = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
          }
        }
      }

      setStatus(newStatus);
      setTimeRemaining(timeText);
    };

    updateStatusAndTimer();

    const interval = setInterval(updateStatusAndTimer, 1000);

    return () => clearInterval(interval);
  }, [template, status, onExpire]);

  const isEnded = status === 'Ended';
  const isLive = status === 'Live';

  // One flow creates both: a single allowed attempt means contest, more means
  // mock. The admin form derives it from the same field.
  const maxAllowedAttempts = Number(template?.maxAllowedAttempts) || 1;
  const isContest = maxAllowedAttempts === 1;

  // Prefer the server-computed per-user count. `attemptedBy` is a set, so it
  // can only prove "at least one attempt" — good enough as a fallback for a
  // contest, and the API supplies totalAttempts for mocks.
  const hasAttempted = Array.isArray(template?.attemptedBy)
    && template.attemptedBy.includes(Session.userId);
  const attemptsUsed = typeof template?.totalAttempts === 'number'
    ? template.totalAttempts
    : (hasAttempted ? 1 : 0);
  const isExhausted = attemptsUsed >= maxAllowedAttempts;

  const isDisabled = isEnded || isExhausted;

  const chipColor = {
    Upcoming: theme.palette.tertiary['clr-700'],
    Live: '#009970',
    Ended: '#E2483C',
  }[status];

  const chipText = status;
  const cardClass = (isDisabled || disabled) ? classes.disabledCard : classes.cardContainer;

  const formatDate = (dateString) => dayjs(dateString).format('DD MMM YYYY | hh:mmA');

  const startContest = () => {
    const label = isContest ? 'contest' : 'mock';

    if (isExhausted) {
      snackbar.error(
        isContest
          ? "You have already attempted this contest."
          : "Your maximum attempt limit is exhausted."
      );
    } else if (!template?.isActive) {
      snackbar.error(`This ${label} is not active.`);
    } else if (dayjs(template?.startAt).isAfter(dayjs())) {
      snackbar.error("You can attempt once it goes live.");
    } else if (dayjs(template?.endAt).isBefore(dayjs())) {
      snackbar.error(`The ${label} has ended.`);
    } else {
      setStartInterview(true);
    }
  };

  useEffect(() => {
    const cid = searchParams.get('cid');
    if (!cid || !template._id || disabled) return;
    if (cid === template._id) {
      setStartInterview(true);
    }
  }, [searchParams, template._id, disabled]);

  return (
    <>
      <NoAccessTooltip show={disabled}>
      <Box className={cardClass} onClick={disabled ? undefined : startContest}>
        <img
          src={
            isEnded
              ? 'https://assets.languify.in/images/contestCardDisabled.png'
              : 'https://assets.languify.in/images/contestCardBg.png'
          }
          alt='bg'
          style={{ position: 'absolute', right: 0, top: 0 }}
          height={208}
        />
        <Box className={classes.header}>
          {isExhausted ? (
            <Chip
              content={isContest ? "Attempted" : "Limit reached"}
              bgColor={theme.palette.neutral['clr-600']}
              color={theme.palette.shades['clr-white-900']}
              sx={{ borderRadius: '4px', padding: '4px 8px' }}
            />
          ) : isNeverExpiring ? (
            <Chip
              content="Never Expires"
              bgColor={theme.palette.primary['clr-50']}
              color={theme.palette.primary['clr-800']}
              sx={{ borderRadius: '4px', padding: '4px 8px' }}
            />
          ) : (
            <Chip
              content={chipText}
              bgColor={chipColor}
              color={theme.palette.shades['clr-white-900']}
              sx={{ borderRadius: '4px', padding: '4px 8px' }}
            />
          )}
          {!isEnded && !isNeverExpiring && timeRemaining && (
            <Typography
              variant='body01-bold'
              style={{
                color: isLive ? '#C51407' : theme.palette.tertiary['clr-700']
              }}
            >
              {isLive ? `Ending in: ${timeRemaining}` : `Starting in: ${timeRemaining}`}
            </Typography>
          )}
        </Box>
        <Box className={classes.content}>
          {isContest && (
            <Box display='flex'>
              <Chip
                content="Contest"
                bgColor={theme.palette.primary.main}
                color={theme.palette.shades['clr-white-900']}
                sx={{ borderRadius: '4px', padding: '4px 8px' }}
              />
            </Box>
          )}
          <Typography
            variant='h3-semiBold'
            color='neutral.clr-700'
            className={classes.truncate}
          >
            {template.name}
          </Typography>
          <Box display='flex' flexDirection='column' gap='4px'>
            <Box>
              <Typography variant='body01-bold'>Started on: </Typography>
              <Typography variant='body01-bold' color='primary.main'>
                {formatDate(template.startAt)}
              </Typography>
            </Box>
            <Box>
              <Typography variant='body01-bold'>Available time: </Typography>
              <Typography variant='body01-bold' color='primary.main'>
                30 min
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
      </NoAccessTooltip>

      <StartAssessmentDialog
        onClose={() => setStartInterview(false)}
        open={startInterview}
        template={{
          _id: template?.casemasterProblem,
          name: template?.name,
          type: 'case_master'
        }}
        stateParams={{
          data: 'contest',
          tagId: template?._id
        }}
      />
    </>
  );
};

function TemplateCardSkeleton() {
  const classes = useStyles();

  return (
    <Box className={classes.cardContainer}>
      <Box display="flex">
        <Skeleton variant="rectangular" width="20%" height='30px' style={{ borderRadius: '20px' }} />
      </Box>
      <Box>
        <Box>
          <Skeleton variant="rectangular" width="90%" height='40px' />
        </Box>
        <Box mt='10px'>
          <Skeleton variant="rectangular" width="100%" height='20px' />
        </Box>
      </Box>
    </Box>
  );
}

function List({ templates = [], loader, disabled = false }) {
  return (
    <>
      {loader
        ? Array.from(new Array(3)).map((_, i) => (
          <TemplateCardSkeleton key={i} />
        ))
        : templates.map((template, index) => (
          <CaseMasterContestCard key={index} template={template} disabled={disabled} />
        ))}
    </>
  );
}


const CaseMasterContestCardList = ({
  searchQuery = "", setSearchQuery = () => { }, mocks = [], disabled = false
}) => {
  const [loader] = useState(false);
  const [filteredMock, setFilteredMock] = useState([]);
  const [appliedFilters, setAppliedFilters] = useState({});

  useEffect(() => {
    const isFilterApplied = Object.keys(appliedFilters).length > 0;

    const filtered = mocks.filter((mock) => {
      const nameToSearch = mock.name;

      return (
        (!isFilterApplied ||
          (((dayjs(mock.endAt).diff(dayjs(), 'hour') < 24) && appliedFilters.expiresSoon) ||
            ((dayjs(mock.startAt).diff(dayjs(), 'hour') < 24) && appliedFilters.new))) &&
        (searchQuery === '' || nameToSearch.toLowerCase().includes(searchQuery.toLowerCase()))
      );
    });

    setFilteredMock(filtered);
  }, [mocks, appliedFilters, searchQuery]);

  const handleFilterChange = ({ field }) => {
    setAppliedFilters(prevFilters => {
      const newFilters = { ...prevFilters };

      if (newFilters[field]) {
        delete newFilters[field];
      } else {
        newFilters[field] = true;
      }

      return newFilters;
    });
    setSearchQuery("");
  };

  return !mocks.length ? null : (
    <Box>
      <FilterChips
        title="Filter by:"
        filters={filters}
        appliedFilters={appliedFilters}
        onChange={handleFilterChange}
      />
      <Box height={280} mt={5}>
        {filteredMock.length || loader ? (
          <HorizontalScrollNavigator
            NoResultComponent={NoResultComponent}
            NoResultComponentProps={"No mock found for the selected filter!"}
            childrensLength={filteredMock.length}
            scrollWidth={1400}
          >
            <List
              templates={filteredMock}
              loader={loader}
              disabled={disabled}
            />
          </HorizontalScrollNavigator>
        ) : (
          <NoResultComponent
            description="Nothing here yet! You can attempt mock once it is published for your batch."
          />
        )}
      </Box>
    </Box>
  )
}

export default CaseMasterContestCardList;
